import React, { memo, useMemo, useRef, useEffect, useState, useCallback, useLayoutEffect } from "react";
import styles from './index.module.scss'
import { parseTime } from '@/utils'
import clsx from "clsx";
import { Popover } from 'antd'
import type { MilestoneRender } from '../milestone/index'
import { k_milestoneStatus, getMilestoneStatusLabel, getMilestoneStatusTextColor } from '../milestone/edit'
import * as d3 from "d3";
import { MoneySvg } from "@/components/svgs"
import { getMoneyColorByStatus } from "../controlNodes/util";
import { get } from "lodash";

type Props = {
  data?: API.PROJECT.ContractPayNode[];
  milestones?: MilestoneRender[]
}
const isComplated = (status: string) => {
  if (status === "未开票" || status === "未收款") {
    return false
  }
  return true
}
export const k_payStatus = {
  unstart: 0, // 未开票、未收款 时间内
  pedding: 1, // 已开票
  complete: 2, // 已付款
  overdue: 3,// 未开票、未收款 且 逾期
}
export const getStatusTextColor = (status: number) => {
  if (status === k_payStatus.overdue) {
    return "text-red-500"
  }
  if (status === k_payStatus.pedding) {
    return "text-amber-500"
  }
  if (status === k_payStatus.complete) {
    return "text-green-500"
  }
  return ""
}
export const getStatusBgColor = (status: number) => {
  if (status === k_payStatus.overdue) {
    return "bg-red-500"
  }
  if (status === k_payStatus.pedding) {
    return "bg-amber-500"
  }
  if (status === k_payStatus.complete) {
    return "bg-green-500"
  }
  return "bg-gray-500"
}
interface RenderContractPayNode extends API.PROJECT.ContractPayNode {
  isOutdate: boolean,
  overDueDays: number
}
interface MixRenderNode{
  milestone?: MilestoneRender,
  paynode?: RenderContractPayNode,
  isMilestoneLineShow?: boolean,
  isPaynodeLineShow?: boolean,
  milestoneStatus?: number,
}
interface RenderItem{
  plotnumber: string,
  list: MixRenderNode[],
  linePos?: LinePoint[]
}
interface LinePoint{
  x1?: number,
  y1?: number,
  x2?: number,
  y2?: number,
  code: string
}
interface IDashLineProps{
  lines?: LinePoint[]
}
const DashLine: React.FC<IDashLineProps> = ({lines}) => {
  const svgRef = useRef<SVGSVGElement>(null);
  useEffect(() => {
    if (!lines || !svgRef.current) return
    const rect = svgRef.current.getBoundingClientRect()
    const newLines = lines.filter(v => v.x1 != undefined && v.x2 != undefined && v.y1 != undefined && v.y2 != undefined).map(v => {
      return {
        ...v,
        x1: v.x1! - rect.left + 14,
        x2: v.x2! - rect.left + 15,
        y1: v.y1! - rect.top + 14,
        y2: v.y2! - rect.top + 15,
      }
    })
    const svg = d3.select(svgRef.current)
    svg.selectAll("*").remove()

    // 定义贝塞尔曲线生成器
    const line = d3.line<{x: number, y: number}>()
      .curve(d3.curveBasis) // 使用贝塞尔插值
      .x(d => d.x)
      .y(d => d.y);
    
    // 遍历数据，为每条线生成路径
    newLines.forEach((d, i) => {
      // 构造贝塞尔曲线的控制点（这里简单示例，实际可能需要更复杂的计算）
      const cx = Math.abs((d.x1 - d.x2) / 4)
      // const centerY = Math.abs((d.y1 - d.y2) / 2)
      const centerY = (d.y1! + d.y2!) / 2

      const points = [
        { x: d.x1!, y: d.y1! }, // 起点
        // { x: d.x1 + cx, y: centerY }, // 控制点1（中间点）
        // { x: d.x2 - cx, y: centerY }, // 控制点2（中间点）
        { x: d.x2!, y: d.y2! }  // 终点
      ];

      svg.append("path")
        .datum(points)
        .attr("d", line)
        .attr("stroke", "#d0d0d0")
        .attr("stroke-width", 4)
        .attr("fill", "none")
        .attr("stroke-dasharray", "8,8"); // 虚线样式
      
      
      // svg.append("circle")
      //   .attr("cx", d.x1)
      //   .attr("cy", d.y1) // 初始位置在底部
      //   .attr("r", 20) // 初始半径为0
      //   .attr("fill", "red")
      
      // svg.append("circle")
      //   .attr("cx", d.x2)
      //   .attr("cy", d.y2) // 初始位置在底部
      //   .attr("r", 20) // 初始半径为0
      //   .attr("fill", "green")
      
    })
  }, [lines])

  return <svg ref={svgRef} className="w-full h-full left-0 top-0 absolute"></svg>
}
const ProjectTimeline: React.FC<Props> = memo((props) => {
  const { data, milestones } = props;
  const [renderItems, setRenderItems] = useState<RenderItem[]>([])
  const hasLineData = useRef(false)
  const hasDrawLines = useRef(false)
  const items = useMemo(() => {
    hasLineData.current = false
    hasDrawLines.current = false
    if (!data) return []
    const curDate = new Date()
    curDate.setHours(0, 0, 0)
    const curTime = curDate.getTime()

    const nodes = data.map((item) => {
      let isOutdate = false
      let overDueDays = 0
      const itemDate = new Date(item.date)
      itemDate.setHours(0, 0, 0)
      if (curTime > itemDate.getTime()) {
        isOutdate = true
        const timeDiff = curTime - itemDate.getTime();
        const daysDiff = timeDiff / (1000 * 60 * 60 * 24);
        overDueDays = Math.floor(Math.abs(daysDiff));
      }
      
      let paystatus = k_payStatus.unstart
      if (item.status === "已收款") {
        paystatus = k_payStatus.complete
      } else if (item.status === "已开票") {
        paystatus = k_payStatus.pedding
      } else if (item.status === "未收款" || item.status === "未开票") {
        if (isOutdate) {
          paystatus = k_payStatus.overdue
        }
      }
      return {
        ...item,
        statusNum: paystatus,
        isOutdate,
        overDueDays,
        date: parseTime(item.date, '{y}-{m}-{d}')
      }
    })
    nodes.sort((a, b) => {
      const adate = new Date(a.date)
      const bdate = new Date(b.date)
      return adate.getTime() - bdate.getTime()
    })

    const plotMap = new Map<string, RenderContractPayNode[]>()
    let hasCommonCode = false
    nodes.forEach(v => {
      const key = v.plotnumber
      const prev = plotMap.get(key)
      if (prev) {
        plotMap.set(key, [...prev, v])
      } else {
        plotMap.set(key, [v])
      }
      if (milestones) {
        if (milestones.find(m => m.milestoneCode === v.milestoneCode)) {
          hasCommonCode = true
        }
      }
    })
    if (hasCommonCode){
      hasLineData.current = true
    }
    const plotList: { plotnumber: string, list: RenderContractPayNode[] }[] = []
    plotMap.forEach((val, key) => {
      plotList.push({
        plotnumber: key,
        list: val
      })
    })

    const newList = plotList.map(v => {
      if (!milestones) return {
        plotnumber: v.plotnumber,
        list: v.list.map((node, i) => {
          return {
            milestone: undefined,
            paynode: node,
            isMilestoneLineShow: false,
            isPaynodeLineShow: i < v.list.length - 1,
            milestoneStatus: 0
          }
        })
      }
      let renderList: MixRenderNode[] = []
      let node_i = 0, stone_j = 0
      while (node_i < v.list.length || stone_j < milestones.length) {
        let item: MixRenderNode = {}
        if (node_i >= v.list.length) {
          item.milestone = milestones[stone_j]
          renderList.push(item)
          stone_j++
          continue
        }
        if (stone_j >= milestones.length) {
          item.paynode = v.list[node_i]
          renderList.push(item)
          node_i++
          continue
        }
        const paynode = v.list[node_i]
        const stoneItem = milestones[stone_j]
        if (!stoneItem.milestoneDate || !paynode.date) {
          item.milestone = milestones[stone_j]
          stone_j++

          item.paynode = v.list[node_i]
          node_i++
          renderList.push(item)

          continue
        }
        const paydate = new Date(paynode.date)
        paydate.setHours(0, 0, 0)
        const stoneDate = new Date(stoneItem.milestoneDate)
        stoneDate.setHours(0, 0, 0)

        const paydateTime = paydate.getTime()
        const stoneDateTime = stoneDate.getTime()
        if (paydateTime === stoneDateTime) {
          item.paynode = v.list[node_i]
          item.milestone = milestones[stone_j]
          renderList.push(item)
          node_i++
          stone_j++
        } else if (paydateTime > stoneDateTime) {
          item.milestone = milestones[stone_j]
          renderList.push(item)
          stone_j++
        } else {
          item.paynode = v.list[node_i]
          renderList.push(item)
          node_i++
        }
      }
      let paynodeCount = 0, milestoneCount = 0
      let laststatus = 0
      return {
        plotnumber: v.plotnumber,
        list: renderList.map(renderItem => {
          let status = 0
          if (renderItem.paynode) {
            paynodeCount++
          }
          if (renderItem.milestone) {
            milestoneCount++
            if (renderItem.milestone.status === k_milestoneStatus.finished) {
              status = k_milestoneStatus.finished
            } else if (renderItem.milestone.outDate) {
              status = k_milestoneStatus.overdue
            }
          } else {
            status = laststatus
          }
          laststatus = status
          return {
            ...renderItem,
            isMilestoneLineShow: milestoneCount < milestones.length,
            isPaynodeLineShow: paynodeCount < v.list.length,
            milestoneStatus: status
          }
        })
      }
    })
    setRenderItems(newList)
    return newList
  }, [data, milestones]);
  

  const handleResize = useCallback(() => {
    if (items.length <= 0) return

    const newItems = items.map((item, index) => {
      let linePosMap = new Map<string, LinePoint>()
      item.list.forEach((v, sub_i) => {
        if (v.milestone && v.milestone.milestoneCode) {
          const milestore_key = `milestore-${index}-${sub_i}`
          const milestore_el = document.getElementById(milestore_key);
          if (milestore_el) {
            const rect = milestore_el.getBoundingClientRect();
            const linePos = linePosMap.get(v.milestone.milestoneCode)
            linePosMap.set(v.milestone.milestoneCode, {
              ...linePos,
              code: v.milestone.milestoneCode,
              x1: rect.left,
              y1: rect.top
            })
          }
        }
        if (v.paynode && v.paynode.milestoneCode) {
          const paynode_key = `paynode-${index}-${sub_i}`
          const paynode_el = document.getElementById(paynode_key);
          if (paynode_el) {
            const rect = paynode_el.getBoundingClientRect();
            const linePos = linePosMap.get(v.paynode.milestoneCode)
            linePosMap.set(v.paynode.milestoneCode, {
              ...linePos,
              code: v.paynode.milestoneCode,
              x2: rect.left,
              y2: rect.top
            })
          }
        }
      })
      let linePosArr: LinePoint[] = []
      linePosArr = Array.from(linePosMap.values()).filter(v => v.x1 && v.x2 && v.y1 && v.y2)
      if (hasLineData.current && linePosArr.length > 0) {
        hasDrawLines.current = true
      }
      return {
        ...item,
        linePos: linePosArr
      }
    });
    setRenderItems(newItems)
  }, [items])

  useEffect(() => {
    handleResize()
    window.addEventListener("resize", handleResize)
    return () => {
      window.removeEventListener("resize", handleResize)
    }
  }, [handleResize])

  const renderNodePoint = (item: MixRenderNode) => {
    if (!item.paynode) return <></>
    const color = getMoneyColorByStatus(item.paynode.status)
    if (item.paynode.isOutdate && (item.paynode.status === "未开票" || item.paynode.status === "未收款")) {
      return <Popover content={
        <div>
          <div className="font-bold">{item.paynode.description}</div>
          <div>计划收款日期: <span className="text-blue-400 font-bold">{item.paynode.date}</span></div>
        </div>
      } trigger="hover">
        <div className={clsx(styles['timeline-item-taild'], getStatusBgColor(item.paynode.statusNum), "flex items-center justify-center text-white")}>
          <MoneySvg />
        </div>
      </Popover>
    }
    return <div className={clsx(styles['timeline-item-taild'], getStatusBgColor(item.paynode.statusNum), "flex items-center justify-center text-white")}>
      <MoneySvg />
    </div>
  }

  return (
    <div>
      {renderItems.map((node, node_i) => (
        <div key={node_i} className="bg-gray-100 mb-4 relative">
          <DashLine lines={node.linePos} />
          {node.plotnumber && (
            <div className="bg-dark-bg inline-block text-white px-4 py-1 absolute left-0 top-0">{node.plotnumber}</div>
          )}
          <div className="pt-12">
            <div className="flex mb-2">
              <div className="w-1/2 text-right pr-[130px]">
                <span className="translate-x-1/2 inline-block font-bold">里程碑计划</span>
              </div>
              <div className="w-1/2 text-left pl-[135px]">
                <span className="-translate-x-1/2 inline-block font-bold">收款节点</span>
              </div>
            </div>
            {node.list.map((item, index) => (
              <div key={index} className="flex">
                
                {item.milestone ? (
                  <div className={clsx(styles['timeline-item'], "flex justify-end", item.milestoneStatus === k_milestoneStatus.finished ? styles['is-outdate'] : item.milestoneStatus === k_milestoneStatus.overdue ? styles['is-overdue'] : '')}>
                    <div className={clsx(styles['timeline-item-content'], "text-right pr-[12px]")}>
                      <div>{item.milestone.milestoneName}</div>
                      <div>{item.milestone.precent}</div>
                    </div>
                    <div className={styles['timeline-milestone-sep']}>
                      {item.isMilestoneLineShow && (
                        <div className={styles['timeline-milestone-line-box']}>
                          <div className={styles['timeline-milestone-line']}>
                            <div className={styles['timeline-milestone-line-animate']}></div>
                          </div>
                        </div>
                      )}
                      <div className={clsx(styles['timeline-milestone-taild'])} id={`milestore-${node_i}-${index}`}></div>
                    </div>
                    <div className={clsx(styles['timeline-item-label'], "text-left pl-[12px]")}>
                      <span className={getMilestoneStatusTextColor(item.milestoneStatus)}>
                        {item.milestone.milestoneDate}
                      </span>
                      <div>{getMilestoneStatusLabel(item.milestoneStatus)}</div>
                    </div>
                    
                  </div>
                ) : (
                  <div className={clsx(styles['timeline-item'], (item.milestoneStatus === k_milestoneStatus.finished ? styles['is-outdate'] : item.milestoneStatus === k_milestoneStatus.overdue ? styles['is-overdue'] : ''))}>
                    {
                        item.isMilestoneLineShow && (
                          <div className={styles['empty-milestone-line-box']}>
                            <div className={styles['empty-milestone-line']}>
                              <div className={styles['timeline-milestone-line-animate']}></div>
                            </div>
                          </div>
                          )
                    }
                  </div>
                )}
                {item.paynode ? (
                  <div className={styles['timeline-item']}>
                    <div className={clsx(styles['timeline-item-label'], getStatusTextColor(item.paynode.statusNum))}>
                      {item.paynode.date}
                      <div>{item.paynode.status}</div>
                    </div>

                    <div className={styles['timeline-item-sep']}>
                      {renderNodePoint(item)}

                      {item.isPaynodeLineShow && (
                        <div className={styles['timeline-item-line-box']}>
                          <div className={styles['timeline-item-line']}></div>
                        </div>
                      )}

                    </div>
                    <div className={styles['timeline-item-content']}>
                      {item.paynode.description}
                    </div>
                  </div>
                ) : (
                    <div className={clsx(styles['timeline-item'])}>
                      {
                        item.isPaynodeLineShow && (
                          <div className={styles['empty-line-box']}>
                            <div className={styles['empty-line']}></div>
                          </div>
                        )
                      }
                    </div>
                  )
                }
                
              </div>
            ))}
          </div>
        </div>
      ))}
      
    </div>
  );
});

export default ProjectTimeline;
